import { useI18n } from "@baton/i18n";
import { AlertCircle, AlertTriangle, Check, Copy, Info, X } from "@baton/icons";
import { AnimatePresence, motion, useReducedMotion, type Transition } from "motion/react";
import { memo, useEffect, useRef, useState, type Ref } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../cn.ts";
import { Button } from "../../primitives/buttons/Button/Button.tsx";
import { IconButton } from "../../primitives/buttons/IconButton/IconButton.tsx";
import { useOverlayStage } from "../portalContainer.tsx";
import { durationFor, useToastStore, visibleToasts, type Toast as ToastRecord, type ToastVariant } from "./toastStore.ts";

/**
 * FR-015-091, § Overlays, Toast, `contracts/screens.md` § Toast region, taken from Spectrum UI's
 * Toast Stack (`@spectrumui/toast-stack`, beUI's Animated Toast Stack, Apache-2.0) and themed
 * through the variables of `packages/tokens` (2026-09-15, by the owner's decision): the stack in
 * the bottom inline-end corner, each toast rising in and leaving sideways on the source's spring,
 * dismissed by its close control or by a flick sideways, paused while hovered or focused, the
 * request id with a copy control, and error toasts that never auto-dismiss. The queue and its
 * durations are `toastStore.ts`'s, so a toast pushed from outside React waits its turn.
 *
 * What differs from the source, and why:
 * - the visible toasts are the first three of the queue (`visibleToasts`), where the source shows
 *   the last four, because the contract queues the rest in order;
 * - the timer pauses while the pointer is over a toast or focus is inside it, which the source has
 *   no state for and § Overlays requires;
 * - the variant's glyph is drawn in that variant's text colour with no tile behind it, where the
 *   source mixes the primary and an emerald at low opacity behind a disc; a warning variant is
 *   Baton's own, and the error glyph is the source's AlertCircle. 2026-09-18, by the owner's
 *   report of "extra space": the disc was 28 px of pale fill around a 14 px glyph and the card was
 *   a fixed 384 px whatever it held, so "Test created" sat in a box of mostly nothing. A toast is
 *   now as wide as its words up to that width, and the glyph is the glyph. 2026-10-10: the action
 *   pill, the copy control and the close had followed the glyph tile down to 20 px, under the
 *   24 px target floor of § Density; each is now `--target-min` with its glyph unchanged;
 * - the surface is the raised popover colour at `--toast-radius` with the third elevation, and not
 *   the source's 95 percent card with a backdrop blur, because the theme defines no blur;
 * - the title and description wrap rather than truncate and clamp, because a toast is often the
 *   only statement of a failure;
 * - the source's crossfades between statuses and titles are not carried, because a Baton toast
 *   never changes after it is pushed;
 * - the stack is a portal on the document body (on the component gallery's overlay stage there,
 *   `portalContainer.tsx`) and names itself "Notifications", and every string comes from the catalogue;
 * - the stack is a polite live region and a failure's card is an alert, so a success is read when
 *   the reader is free and a failure interrupts (`contracts/screens.md` § Toast region: polite for
 *   successes, assertive for failures), where the source announces nothing.
 */
const STACK_SPRING: Transition = {
  type: "spring",
  stiffness: 420,
  damping: 34,
  mass: 0.75,
};

const EXIT_EASE = [0.16, 1, 0.3, 1] as const;

const ICONS = { info: Info, success: Check, warning: AlertTriangle, error: AlertCircle } as const;

const ICON_TONE: Readonly<Record<ToastVariant, string>> = {
  info: "text-accent-text",
  success: "text-accent-green-text",
  warning: "text-accent-amber-text",
  error: "text-accent-red-text",
};

export function ToastRegion() {
  const { t } = useI18n();
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);
  const overlayStage = useOverlayStage();
  return createPortal(
    <section data-slot="toast-region" aria-label={t("ui.toast.region")} className="pointer-events-none fixed end-4 bottom-6 z-(--layer-toast) max-w-[min(var(--toast-width),calc(100%_-_var(--space-4)_*_2))]">
      <ol aria-live="polite" aria-atomic="false" className="flex flex-col-reverse items-end gap-2">
        <AnimatePresence initial={false} mode="popLayout">
          {visibleToasts(toasts).map((item, index) => (
            <ToastItem
              key={item.id}
              toast={item}
              index={index}
              onDismiss={() => {
                dismiss(item.id);
              }}
            />
          ))}
        </AnimatePresence>
      </ol>
    </section>,
    overlayStage ?? document.body,
  );
}

export interface ToastItemProps {
  readonly toast: ToastRecord;
  readonly onDismiss: () => void;
  /** The toast's place in the stack, which keeps the nearer toast above the one behind it. */
  readonly index?: number;
  readonly ref?: Ref<HTMLLIElement>;
}

export const ToastItem = memo(function ToastItem({ toast: item, onDismiss, index = 0, ref }: ToastItemProps) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const duration = durationFor(item);
  const remaining = useRef(duration);
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  // The auto-dismiss timer runs only while the toast is neither hovered nor focused; each pause keeps what was left.
  const paused = hovered || focused;
  useEffect(() => {
    if (remaining.current === null || paused) return;
    const left = remaining.current;
    const started = Date.now();
    const timer = window.setTimeout(() => {
      dismissRef.current();
    }, left);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, left - (Date.now() - started));
    };
  }, [paused]);

  const Icon = ICONS[item.variant];
  const { action } = item;
  return (
    <motion.li
      ref={ref}
      layout
      data-slot="toast"
      data-variant={item.variant}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 22, scale: 0.96, filter: "blur(10px)" }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, x: 32, scale: 0.96, filter: "blur(8px)", transition: { duration: 0.18, ease: EXIT_EASE } }}
      transition={STACK_SPRING}
      drag={reduce ? false : "x"}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.18}
      onDragEnd={(_, info) => {
        if (Math.abs(info.offset.x) > 72 || Math.abs(info.velocity.x) > 520) onDismiss();
      }}
      onPointerEnter={() => {
        setHovered(true);
      }}
      onPointerLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      className="pointer-events-auto relative will-change-transform"
      style={{ zIndex: 20 - index }}
    >
      <div role={item.variant === "error" ? "alert" : undefined} className="relative overflow-hidden rounded-(--toast-radius) border border-border bg-popover py-(--space-3) ps-(--space-3) pe-(--space-2) text-popover-foreground shadow-lg">
        <div className="flex items-start gap-(--space-2)">
          <motion.span layout className={cn("inline-flex size-(--toast-icon-size) shrink-0 items-center justify-center", ICON_TONE[item.variant])} aria-hidden="true">
            <Icon className="size-(--icon-sm)" />
          </motion.span>
          <div className="min-w-0 flex-1 py-px">
            <p className="text-sm leading-5 font-medium text-foreground">{item.title}</p>
            {item.description !== undefined && <p className="mt-0.5 text-xs leading-4 text-muted-foreground">{item.description}</p>}
            {(action !== undefined || item.requestId !== undefined) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {action !== undefined && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-(--target-min) rounded-full bg-secondary px-3 text-xs"
                    onClick={() => {
                      // The action is taken once: the toast leaves with it, so an Undo or a Restore cannot be sent again from the same card (2026-10-10).
                      action.onSelect();
                      onDismiss();
                    }}
                  >
                    {action.label}
                  </Button>
                )}
                {item.requestId !== undefined && (
                  <>
                    <span className="font-mono text-xs text-muted-foreground">{t("ui.requestId", { id: item.requestId })}</span>
                    <IconButton
                      size="sm"
                      className="size-(--target-min) rounded-full"
                      icon={<Copy />}
                      label={copied ? t("ui.copied") : t("ui.copyRequestId")}
                      onClick={() => {
                        void navigator.clipboard.writeText(item.requestId ?? "").then(() => {
                          setCopied(true);
                        });
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          <IconButton size="sm" className="size-(--target-min) shrink-0 rounded-(--radius-sm) text-muted-foreground hover:text-foreground" icon={<X />} label={t("ui.toast.dismiss")} onClick={onDismiss} />
        </div>
      </div>
    </motion.li>
  );
});
